/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useState, useEffect } from 'react';
import { useVideoContext } from '../../contexts/videoProvider/videoContext';

/*
 * This hook allows components to reliably use the 'mediaStreamTrack' property of
 * an AudioTrack or a VideoTrack. Whenever 'localTrack.restart(...)' is called, it
 * will replace the mediaStreamTrack property of the localTrack, but the localTrack
 * object will stay the same. Therefore this hook is needed in order for components
 * to re-render in response to the mediaStreamTrack changing.
 *
 * Note: mediaStreamTrack is only available for Twilio VideoProvider.
 */
export function useMediaStreamTrack(track?: VideoProvider.ITrack) {
  const { videoProvider } = useVideoContext();
  const [mediaStreamOrTrack, setMediaStreamOrTrack] = useState<MediaStream | MediaStreamTrack | undefined>();
  const [isCameraFrontFacing, setIsCameraFrontFacing] = useState(false);

  useEffect(() => {
    setMediaStreamOrTrack(videoProvider.getMediaTrackOrStream(track));

    if (track) {
      const handleStarted = () => setMediaStreamOrTrack(videoProvider.getMediaTrackOrStream(track));
      videoProvider.onTrackStarted(track, handleStarted);
      return () => {
        videoProvider.onTrackStopped(track, handleStarted);
      };
    }
    return;
    // track?.audioStream is needed as dependency because Zoom doesn't provide a listener for
    // audioStream but updates it whenever the stream changes
  }, [track, track?.audioStream]);

  useEffect(() => {
    if (mediaStreamOrTrack) {
      setIsCameraFrontFacing(videoProvider.isCameraFrontFacing(mediaStreamOrTrack));
    }
  }, [mediaStreamOrTrack]);

  return { mediaStreamOrTrack, isCameraFrontFacing };
}
